import React, { useState, useRef } from "react";
import { uploadFile } from "../../utils/storage";
import toast from "react-hot-toast";

export default function AdminUploads() {
  const [uploads, setUploads] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileRef = useRef();

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    try {
      const results = await Promise.all(
        files.map(file => uploadFile(file, "media", setProgress))
      );
      setUploads(u => [...results.map(r => ({ url: r.url, name: files[0].name, type: files[0].type })), ...u]);
      toast.success(`${files.length} file(s) uploaded.`);
    } catch {
      toast.error("Upload failed.");
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const copyUrl = (url) => {
    navigator.clipboard.writeText(url).then(() => toast.success("URL copied!"));
  };

  return (
    <div>
      <div style={{ marginBottom: 32 }}>
        <div style={{ fontFamily: "var(--font-mono)", fontSize: "10px", letterSpacing: "0.2em", textTransform: "uppercase", color: "var(--gold)", marginBottom: 8 }}>
          Media
        </div>
        <h2 style={{ fontSize: "1.6rem" }}>Uploads & Media</h2>
      </div>

      {/* Upload area */}
      <div
        onClick={() => fileRef.current?.click()}
        style={{
          border: "2px dashed var(--border-light)",
          borderRadius: "var(--radius-lg)",
          padding: "60px 40px",
          textAlign: "center",
          cursor: "pointer",
          transition: "all 0.25s",
          marginBottom: 32,
          background: "var(--bg-card)",
        }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = "var(--gold-muted)"; e.currentTarget.style.background = "var(--bg-elevated)"; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--border-light)"; e.currentTarget.style.background = "var(--bg-card)"; }}
        onDragOver={e => e.preventDefault()}
        onDrop={async (e) => {
          e.preventDefault();
          const files = Array.from(e.dataTransfer.files);
          if (files.length) {
            fileRef.current.files = e.dataTransfer.files;
            handleUpload({ target: { files } });
          }
        }}
      >
        <input
          type="file"
          ref={fileRef}
          multiple
          accept="image/*,.pdf"
          style={{ display: "none" }}
          onChange={handleUpload}
        />
        <div style={{ fontSize: "2rem", color: "var(--gold)", marginBottom: 16 }}>⊕</div>
        <div style={{ fontFamily: "var(--font-body)", fontSize: "15px", color: "var(--text-primary)", marginBottom: 8 }}>
          {uploading ? `Uploading... ${progress}%` : "Click to upload or drag and drop"}
        </div>
        <div style={{ fontFamily: "var(--font-mono)", fontSize: "11px", color: "var(--text-muted)" }}>
          Images (JPG, PNG, WebP) and PDFs
        </div>
        {uploading && (
          <div style={{ marginTop: 16, height: 3, background: "var(--border)", borderRadius: 2, maxWidth: 200, margin: "16px auto 0" }}>
            <div style={{ height: "100%", width: `${progress}%`, background: "var(--gold)", borderRadius: 2, transition: "width 0.2s" }} />
          </div>
        )}
      </div>

      {uploads.length === 0 ? (
        <div style={{
          background: "var(--bg-card)",
          border: "1px solid var(--border)",
          borderRadius: "var(--radius)",
          padding: "60px 40px",
          textAlign: "center",
        }}>
          <p style={{ fontFamily: "var(--font-mono)", fontSize: "12px", color: "var(--text-muted)" }}>
            No files uploaded yet in this session.<br />
            Files persist in Firebase Storage.
          </p>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 16 }}>
          {uploads.map((file, i) => (
            <div key={i} style={{
              background: "var(--bg-card)",
              border: "1px solid var(--border)",
              borderRadius: "var(--radius)",
              overflow: "hidden",
            }}>
              {file.type?.startsWith("image/") ? (
                <img src={file.url} alt={file.name} style={{ width: "100%", height: 150, objectFit: "cover" }} />
              ) : (
                <div style={{ height: 150, display: "flex", alignItems: "center", justifyContent: "center", background: "var(--bg-secondary)", fontSize: "3rem" }}>
                  📄
                </div>
              )}
              <div style={{ padding: "12px" }}>
                <div style={{ fontSize: "12px", color: "var(--text-secondary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginBottom: 8 }}>
                  {file.name}
                </div>
                <button
                  onClick={() => copyUrl(file.url)}
                  className="btn btn-ghost"
                  style={{ width: "100%", justifyContent: "center", padding: "6px", fontSize: "10px" }}
                >
                  Copy URL
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{ marginTop: 24, padding: "16px 20px", background: "var(--bg-secondary)", border: "1px solid var(--border)", borderRadius: "var(--radius)" }}>
        <p style={{ fontFamily: "var(--font-mono)", fontSize: "11px", color: "var(--text-muted)", lineHeight: 1.7 }}>
          ℹ️ Files are stored permanently in Firebase Storage. View all files in the{" "}
          <a href="https://console.firebase.google.com" target="_blank" rel="noreferrer" style={{ color: "var(--gold)" }}>
            Firebase Console
          </a>
          {" "}under Storage.
        </p>
      </div>
    </div>
  );
}
